import { equals2, go, mapL, some, tap } from "fxjs/es";
import { CommonNS } from "../../../../Common/CommonNS.js";
import { VONS } from "../../VO/VONS.js";
import { DATA_KEY_IS_SCALED } from "./const.js";
import { TransformManipulator } from "./TransformManipulator.js";
export class ScaleTransform {
  constructor({ svg_el, transform_manipulator }) {
    this._observer_set = new Set();
    this._svg_el = svg_el;
    this._transform_manipulator = transform_manipulator;
  }
  subscribe(observer) {
    if (this._observer_set.has(observer)) {
      throw new Error(`이미 등록된 observer 입니다.`);
    }
    this._observer_set.add(observer);
    return () => {
      this._observer_set.delete(observer);
    };
  }
  getIsScaled(el) {
    return equals2("true")(el.dataset[DATA_KEY_IS_SCALED]);
  }
  markIsScaled(el) {
    el.dataset[DATA_KEY_IS_SCALED] = "true";
  }
  scaleSingleElFitTo({ el, view_port_target_size }) {
    const bbox = el.getBBox();
    if (bbox.width === 0 || bbox.height === 0) {
      throw new Error(`엘리먼트의 크기가 없습니다.`);
    }
    const el_ctm = el.getCTM();
    if (el_ctm == null) {
      throw new Error(`엘리먼트의 CTM 을 구할 수 없습니다.`);
    }
    const [point1, point2] = go(
      [
        [bbox.x, bbox.y],
        [bbox.x + bbox.width, bbox.y],
        [bbox.x + bbox.width, bbox.y + bbox.height],
        [bbox.x, bbox.y + bbox.height],
      ],
      mapL(CommonNS.UtilNS.createSVGPoint(this._svg_el)),
      mapL((point) => point.matrixTransform(el_ctm)),
      CommonNS.UtilNS.calculateMinMaxXY,
      ({ min_x, min_y, max_x, max_y }) => [
        [min_x, min_y],
        [max_x, max_y],
      ],
      mapL(CommonNS.UtilNS.createSVGPoint(this._svg_el))
    );
    const center_point = CommonNS.UtilNS.createSVGPoint(this._svg_el)([
      (point1.x + point2.x) / 2,
      (point1.y + point2.y) / 2,
    ]);
    const view_port_el_width = Math.abs(point1.x - point2.x);
    const view_port_el_height = Math.abs(point1.y - point2.y);
    const sx = view_port_target_size.width / view_port_el_width;
    const sy = view_port_target_size.height / view_port_el_height;
    const s = Math.min(sx, sy);
    const post_transform = this._svg_el.createSVGTransform();
    post_transform.setTranslate(center_point.x, center_point.y);
    const { matrix: post_transform_matrix } = post_transform;
    const pre_transform_matrix = post_transform_matrix.inverse();
    const scale_transform = this._svg_el.createSVGTransform();
    scale_transform.setScale(s, s);
    const scale_transform_matrix = post_transform_matrix
      .multiply(scale_transform.matrix)
      .multiply(pre_transform_matrix);
    const el_fixed_transform_matrix = (() => {
      const fixed_transform = this._transform_manipulator.getFixedTransform(el);
      if (fixed_transform == null) {
        return (
          CommonNS.UtilNS.getConsolidatedTransformMatrix(el) ??
          this._svg_el.createSVGMatrix()
        );
      }
      return fixed_transform.matrix;
    })();
    const el_transform_matrix = el_fixed_transform_matrix
      .multiply(el_ctm.inverse())
      .multiply(scale_transform_matrix)
      .multiply(el_ctm)
      .multiply(el_fixed_transform_matrix.inverse());
    this._transform_manipulator.setExtendedTransformBeforeBase(
      el_transform_matrix,
      el
    );
    this._transform_manipulator.mergeExtendedTransformToBase(el);
    this._notify({
      el,
    });
    el.dispatchEvent(
      new CustomEvent(`@mp/sticker-editor/transform`, {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          el,
          transform_type: "scale",
          transform_status: "end",
        },
      })
    );
  }
  flipHorizontal(el) {
    const bbox = el.getBBox();
    this._transform_manipulator.initTransform(el);
    const el_fixed_transform =
      this._transform_manipulator.getFixedTransform(el);
    if (el_fixed_transform == null) {
      throw new Error(`엘리먼트의 fixed transform 을 찾을 수 없습니다.`);
    }
    const { matrix: el_fixed_transform_matrix } = el_fixed_transform;
    const { min_x, min_y, max_x, max_y } = go(
      [
        [bbox.x, bbox.y],
        [bbox.x + bbox.width, bbox.y],
        [bbox.x + bbox.width, bbox.y + bbox.height],
        [bbox.x, bbox.y + bbox.height],
      ],
      mapL(CommonNS.UtilNS.createSVGPoint(this._svg_el)),
      mapL((point) => point.matrixTransform(el_fixed_transform_matrix)),
      CommonNS.UtilNS.calculateMinMaxXY,
      tap(({ min_x, min_y, max_x, max_y }) => {
        if (
          some((a) => Number.isNaN(a) || !Number.isFinite(a))([
            min_x,
            min_y,
            max_x,
            max_y,
          ])
        ) {
          throw new Error(`엘리먼트의 bounding box 를 계산할 수 없습니다.`);
        }
      })
    );
    const origin_x = (min_x + max_x) / 2;
    const origin_y = (min_y + max_y) / 2;
    const post_transform = this._svg_el.createSVGTransform();
    post_transform.setTranslate(origin_x, origin_y);
    const { matrix: post_transform_matrix } = post_transform;
    const pre_transform_matrix = post_transform_matrix.inverse();
    const scale_transform = this._svg_el.createSVGTransform();
    scale_transform.setScale(-1, 1);
    const { matrix: scale_transform_matrix } = scale_transform;
    const el_transform_matrix = post_transform_matrix
      .multiply(scale_transform_matrix)
      .multiply(pre_transform_matrix);
    this._transform_manipulator.setFixedTransform(
      el_transform_matrix.multiply(el_fixed_transform_matrix),
      el
    );
    this._notify({
      el,
    });
    el.dispatchEvent(
      new CustomEvent(`@mp/sticker-editor/transform`, {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          el,
          transform_type: "scale",
          transform_status: "end",
        },
      })
    );
  }
  flipVertical(el) {
    const bbox = el.getBBox();
    this._transform_manipulator.initTransform(el);
    const el_fixed_transform =
      this._transform_manipulator.getFixedTransform(el);
    if (el_fixed_transform == null) {
      throw new Error(`엘리먼트의 fixed transform 을 찾을 수 없습니다.`);
    }
    const { matrix: el_fixed_transform_matrix } = el_fixed_transform;
    const { min_x, min_y, max_x, max_y } = go(
      [
        [bbox.x, bbox.y],
        [bbox.x + bbox.width, bbox.y],
        [bbox.x + bbox.width, bbox.y + bbox.height],
        [bbox.x, bbox.y + bbox.height],
      ],
      mapL(CommonNS.UtilNS.createSVGPoint(this._svg_el)),
      mapL((point) => point.matrixTransform(el_fixed_transform_matrix)),
      CommonNS.UtilNS.calculateMinMaxXY,
      tap(({ min_x, min_y, max_x, max_y }) => {
        if (
          some((a) => Number.isNaN(a) || !Number.isFinite(a))([
            min_x,
            min_y,
            max_x,
            max_y,
          ])
        ) {
          throw new Error(`엘리먼트의 bounding box 를 계산할 수 없습니다.`);
        }
      })
    );
    const origin_x = (min_x + max_x) / 2;
    const origin_y = (min_y + max_y) / 2;
    const post_transform = this._svg_el.createSVGTransform();
    post_transform.setTranslate(origin_x, origin_y);
    const { matrix: post_transform_matrix } = post_transform;
    const pre_transform_matrix = post_transform_matrix.inverse();
    const scale_transform = this._svg_el.createSVGTransform();
    scale_transform.setScale(1, -1);
    const { matrix: scale_transform_matrix } = scale_transform;
    const el_transform_matrix = post_transform_matrix
      .multiply(scale_transform_matrix)
      .multiply(pre_transform_matrix);
    this._transform_manipulator.setFixedTransform(
      el_transform_matrix.multiply(el_fixed_transform_matrix),
      el
    );
    this._notify({
      el,
    });
    el.dispatchEvent(
      new CustomEvent(`@mp/sticker-editor/transform`, {
        bubbles: true,
        cancelable: true,
        composed: false,
        detail: {
          el,
          transform_type: "scale",
          transform_status: "end",
        },
      })
    );
  }
  _notify(transform_vo) {
    for (const observer of this._observer_set) {
      CommonNS.UtilNS.tryNoop(() => observer(transform_vo));
    }
  }
}
